import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import { ApiError, type Product, classificationApi } from '../../services/api';
import { TicPicker } from '../components/TicPicker';

interface ClassificationCounts {
  total: number;
  pending: number;
  auto: number;
  override: number;
}

const BLUE = '#2563EB';
const RED = '#E93544';
const ORANGE = '#E97135';
const GREEN = '#1DAF61';
const AMBER = '#F6B51E';
const PURPLE = '#7C3AED';
const TEXT_DARK = '#0E121B';
const TEXT_MUTED = '#525866';
const BORDER = '#E1E4EA';
const BORDER_CARD = '#CACFD8';

// ── Icons ─────────────────────────────────────────────────────────────────────

function Box3Icon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
      <path d="M3 8l9 5 9-5" />
      <line x1="12" y1="13" x2="12" y2="21" />
    </svg>
  );
}

function EyeLineIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckboxCircleIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}

function UserLineIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function AlertLineIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function Search2Icon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// Figma layer named this "meta-line" — Remix's meta-line is the Meta/Facebook
// logo, which doesn't fit a tab-count badge, so a generic tally-list glyph is
// used here instead.
function MetaLineIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function ChevronDoubleIcon({ flip }: { flip?: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: flip ? 'rotate(180deg)' : undefined }}
    >
      <polyline points="11 17 6 12 11 7" />
      <polyline points="18 17 13 12 18 7" />
    </svg>
  );
}

function ChevronIcon({ flip }: { flip?: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: flip ? 'rotate(180deg)' : undefined }}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

// ── Metric cards ──────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: JSX.Element;
  iconBg: string;
  iconColor: string;
  label: string;
  value: number | string;
}
function StatCard({ icon, iconBg, iconColor, label, value }: StatCardProps): JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: '#fff',
        border: `1px solid ${BORDER_CARD}`,
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(14,18,27,.05)',
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 6,
          background: iconBg,
          color: iconColor,
          padding: 8,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: TEXT_MUTED }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 600, color: TEXT_DARK }}>{value}</div>
      </div>
    </div>
  );
}

// ── Pending TIC alert banner ──────────────────────────────────────────────────

interface PendingAlertBannerProps {
  count: number;
  onReviewNow: () => void;
  onDismiss: () => void;
}
function PendingAlertBanner({
  count,
  onReviewNow,
  onDismiss,
}: PendingAlertBannerProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#FFEBEC',
        border: `1px solid ${RED}`,
        borderRadius: 8,
        padding: 10,
        marginBottom: 20,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: 6,
          background: RED,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AlertLineIcon />
      </div>
      <div style={{ flex: 1, minWidth: 200, fontSize: 14, fontWeight: 600, color: TEXT_DARK }}>
        {count} product{count !== 1 ? 's' : ''} need a TIC code before their tax can be calculated.
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={onReviewNow}
          style={{
            background: RED,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Review Now
        </button>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            background: '#fff',
            color: TEXT_DARK,
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ── Filter tabs + search ──────────────────────────────────────────────────────

interface TabDef {
  key: string;
  label: string;
  count: number;
  badgeColor: string;
}

function TabButton({
  tab,
  active,
  onClick,
}: { tab: TabDef; active: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 6,
        border: 'none',
        background: active ? '#fff' : 'transparent',
        boxShadow: active ? '0 1px 2px rgba(14,18,27,.08)' : 'none',
        fontSize: 13,
        fontWeight: 600,
        color: active ? TEXT_DARK : TEXT_MUTED,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {tab.label}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          background: tab.badgeColor,
          color: '#fff',
          borderRadius: 999,
          padding: '2px 7px',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        <MetaLineIcon />
        {tab.count}
      </span>
    </button>
  );
}

interface FilterBarProps {
  tabs: TabDef[];
  filter: string;
  onFilter: (key: string) => void;
  search: string;
  onSearch: (v: string) => void;
  mobile: boolean;
}
function FilterBar({
  tabs,
  filter,
  onFilter,
  search,
  onSearch,
  mobile,
}: FilterBarProps): JSX.Element {
  const [main, overrides] = [tabs.slice(0, 3), tabs[3]];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: 16,
        flexWrap: mobile ? 'wrap' : 'nowrap',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: '#F5F7FA',
          padding: 4,
          borderRadius: 8,
          flexWrap: 'wrap',
        }}
      >
        {main.map((tab) => (
          <TabButton
            key={tab.key}
            tab={tab}
            active={filter === tab.key}
            onClick={() => onFilter(tab.key)}
          />
        ))}
        {overrides && (
          <>
            <div style={{ width: 1, height: 20, background: BORDER_CARD, margin: '0 2px' }} />
            <TabButton
              tab={overrides}
              active={filter === overrides.key}
              onClick={() => onFilter(overrides.key)}
            />
          </>
        )}
      </div>
      <div style={{ position: 'relative', width: mobile ? '100%' : 300, flexShrink: 0 }}>
        <span
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: TEXT_MUTED,
            display: 'flex',
          }}
        >
          <Search2Icon />
        </span>
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search ..."
          style={{
            width: '100%',
            padding: '8px 12px 8px 30px',
            fontSize: 13,
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            background: '#fff',
            outline: 'none',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
      </div>
    </div>
  );
}

// ── Table cells ───────────────────────────────────────────────────────────────

const COLS = {
  checkbox: '0 0 36px',
  product: '2 1 0',
  channel: '0 0 100px',
  tic: '1.4 1 0',
  confidence: '0 0 90px',
  status: '0 0 120px',
  revenue: '0 0 120px',
  actions: '0 0 170px',
};

const HEADERS: { label: string; flex: string }[] = [
  { label: 'Product', flex: COLS.product },
  { label: 'Channel', flex: COLS.channel },
  { label: 'TIC / Suggestion', flex: COLS.tic },
  { label: 'Confidence', flex: COLS.confidence },
  { label: 'Status', flex: COLS.status },
  { label: 'Revenue (90d)', flex: COLS.revenue },
  { label: 'Actions', flex: COLS.actions },
];

function pillStyle(bg: string, color: string): React.CSSProperties {
  return {
    display: 'inline-block',
    background: bg,
    color,
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 999,
  };
}

function ChannelBadge({ channel }: { channel: string }): JSX.Element {
  const c = channel.toLowerCase();
  const cfg =
    c === 'amazon'
      ? { bg: '#FFF1EB', color: ORANGE, label: 'Amazon' }
      : c === 'shopify'
        ? { bg: '#E0FAEC', color: GREEN, label: 'Shopify' }
        : { bg: '#F5F7FA', color: TEXT_MUTED, label: channel };
  return <span style={pillStyle(cfg.bg, cfg.color)}>{cfg.label}</span>;
}

function TicCell({ product: p }: { product: Product }): JSX.Element {
  if (p.current_tic_code) {
    return (
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT_DARK }}>{p.current_tic_code}</div>
        {p.suggested_tic_name && (
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>
            {p.suggested_tic_name}
          </div>
        )}
      </div>
    );
  }
  if (p.suggested_tic_code) {
    return (
      <div>
        <div style={{ fontSize: 13 }}>
          <span style={{ color: TEXT_MUTED }}>Suggest: </span>
          <span style={{ color: BLUE, fontWeight: 700 }}>{p.suggested_tic_code}</span>
        </div>
        {p.suggested_tic_name && (
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>
            {p.suggested_tic_name}
          </div>
        )}
      </div>
    );
  }
  return <span style={{ fontSize: 13, color: BORDER_CARD }}>No suggestion</span>;
}

function ConfidenceCell({ score }: { score: number | null | undefined }): JSX.Element {
  if (score == null)
    return <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_MUTED }}>Manual</span>;
  const pct = Math.round(score * 100);
  const color = pct >= 90 ? GREEN : pct >= 70 ? AMBER : RED;
  return <span style={{ fontSize: 13, fontWeight: 700, color }}>{pct}%</span>;
}

function StatusPill({ method }: { method: string }): JSX.Element {
  const cfg =
    method === 'pending_review'
      ? { bg: '#FFEBEC', color: RED, label: 'Needs review' }
      : method === 'auto'
        ? { bg: '#E0FAEC', color: GREEN, label: 'Auto' }
        : method === 'override'
          ? { bg: '#FFF1EB', color: ORANGE, label: 'Override' }
          : { bg: '#F5F7FA', color: TEXT_MUTED, label: 'Manual' };
  return <span style={pillStyle(cfg.bg, cfg.color)}>{cfg.label}</span>;
}

function btnStyle(kind: 'primary' | 'secondary'): React.CSSProperties {
  return kind === 'primary'
    ? {
        background: BLUE,
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      }
    : {
        background: '#fff',
        color: TEXT_DARK,
        border: `1px solid ${BORDER}`,
        borderRadius: 6,
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      };
}

interface ActionsCellProps {
  product: Product;
  onAccept: () => void;
  onEdit: () => void;
  saving: boolean;
}
function ActionsCell({ product: p, onAccept, onEdit, saving }: ActionsCellProps): JSX.Element {
  if (p.classification_method === 'pending_review') {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {p.suggested_tic_code && (
          <button type="button" onClick={onAccept} disabled={saving} style={btnStyle('primary')}>
            Accept
          </button>
        )}
        <button type="button" onClick={onEdit} disabled={saving} style={btnStyle('secondary')}>
          Classify
        </button>
      </div>
    );
  }
  return (
    <button type="button" onClick={onEdit} disabled={saving} style={btnStyle('secondary')}>
      Edit
    </button>
  );
}

// ── Table row ─────────────────────────────────────────────────────────────────

interface TableRowProps {
  product: Product;
  isSelected: boolean;
  isSuccess: boolean;
  onToggle: () => void;
  onAccept: () => void;
  onChangeTic: () => void;
  saving: boolean;
}
function TableRow({
  product: p,
  isSelected,
  isSuccess,
  onToggle,
  onAccept,
  onChangeTic,
  saving,
}: TableRowProps): JSX.Element {
  const [hov, setHov] = useState(false);
  if (isSuccess) {
    return (
      <div
        style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9', background: '#E0FAEC' }}
      >
        <span style={{ fontSize: 13, color: GREEN, fontWeight: 600 }}>TIC code assigned</span>
      </div>
    );
  }
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '13px 20px 13px 0',
        borderBottom: '1px solid #F1F5F9',
        background: isSelected ? '#EFF6FF' : hov ? '#FAFAFA' : '#fff',
      }}
    >
      <div style={{ flex: COLS.checkbox, paddingLeft: 20 }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          style={{ cursor: 'pointer', accentColor: BLUE }}
        />
      </div>
      <div style={{ flex: COLS.product, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: TEXT_DARK,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {p.product_name}
        </div>
        <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>{p.channel_product_id}</div>
      </div>
      <div style={{ flex: COLS.channel }}>
        <ChannelBadge channel={p.channel} />
      </div>
      <div style={{ flex: COLS.tic }}>
        <TicCell product={p} />
      </div>
      <div style={{ flex: COLS.confidence }}>
        <ConfidenceCell score={p.confidence_score} />
      </div>
      <div style={{ flex: COLS.status }}>
        <StatusPill method={p.classification_method} />
      </div>
      <div style={{ flex: COLS.revenue }}>
        <span style={{ fontSize: 16, fontWeight: 500, color: TEXT_DARK }}>
          {p.estimated_revenue_impact != null
            ? `$${p.estimated_revenue_impact.toLocaleString()}`
            : '—'}
        </span>
      </div>
      <div style={{ flex: COLS.actions }}>
        <ActionsCell product={p} onAccept={onAccept} onEdit={onChangeTic} saving={saving} />
      </div>
    </div>
  );
}

const SKELETON_ROW_IDS = ['sk-row-1', 'sk-row-2', 'sk-row-3', 'sk-row-4', 'sk-row-5'];
const SKELETON_CELL_IDS = [
  'sk-cell-1',
  'sk-cell-2',
  'sk-cell-3',
  'sk-cell-4',
  'sk-cell-5',
  'sk-cell-6',
];

function LoadingRows(): JSX.Element {
  return (
    <div style={{ padding: '8px 20px' }}>
      {SKELETON_ROW_IDS.map((rowId) => (
        <div
          key={rowId}
          style={{
            display: 'flex',
            gap: 14,
            padding: '14px 0',
            borderBottom: '1px solid #F8FAFC',
            alignItems: 'center',
          }}
        >
          <div className="skeleton-box" style={{ width: 16, height: 16, borderRadius: 3 }} />
          <div style={{ flex: 2 }}>
            <div className="skeleton-box" style={{ width: 200, height: 13, marginBottom: 6 }} />
            <div className="skeleton-box" style={{ width: 100, height: 10 }} />
          </div>
          {SKELETON_CELL_IDS.map((cellId) => (
            <div
              key={`${rowId}-${cellId}`}
              className="skeleton-box"
              style={{ flex: 1, height: 20, borderRadius: 6 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ filter }: { filter: string }): JSX.Element {
  const msg =
    filter === 'pending_review'
      ? { title: 'All products classified', body: 'No products need a TIC code right now.' }
      : { title: 'No products found', body: 'Try adjusting your filter or search.' };
  return (
    <div
      style={{
        padding: '48px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK }}>{msg.title}</div>
      <div style={{ fontSize: 13, color: TEXT_MUTED }}>{msg.body}</div>
    </div>
  );
}

interface ErrorStateProps {
  message: string;
  errorStatus: number | null;
  onRetry: () => void;
}
function ErrorState({ message, errorStatus, onRetry }: ErrorStateProps): JSX.Element {
  if (errorStatus === 403) {
    return (
      <div
        style={{
          padding: '32px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div style={{ fontSize: 13, color: RED, fontWeight: 600 }}>Access denied</div>
        <div style={{ fontSize: 12, color: TEXT_MUTED, textAlign: 'center' }}>
          You don't have permission to view this — ask your organization admin for access.
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: '32px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 13, color: RED, fontWeight: 600 }}>Failed to load products</div>
      <div style={{ fontSize: 12, color: TEXT_MUTED }}>{message}</div>
      <button type="button" onClick={onRetry} style={btnStyle('secondary')}>
        Try again
      </button>
    </div>
  );
}

interface BulkBarProps {
  count: number;
  onAssign: () => void;
  onClear: () => void;
  busy: boolean;
}
function BulkBar({ count, onAssign, onClear, busy }: BulkBarProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 20px',
        background: '#EFF6FF',
        borderBottom: '1px solid #BFDBFE',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>
        {count} product{count !== 1 ? 's' : ''} selected
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onAssign} disabled={busy} style={btnStyle('primary')}>
          {busy ? 'Assigning…' : 'Assign TIC code'}
        </button>
        <button type="button" onClick={onClear} style={btnStyle('secondary')}>
          Deselect all
        </button>
      </div>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function pageNumbers(current: number, totalPages: number): number[] {
  const start = Math.max(1, current - 2);
  const end = Math.min(totalPages, current + 2);
  const nums: number[] = [];
  for (let i = start; i <= end; i++) nums.push(i);
  return nums;
}

function PageArrowButton({
  children,
  disabled,
  onClick,
}: { children: JSX.Element; disabled: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        border: 'none',
        background: 'transparent',
        color: disabled ? BORDER_CARD : TEXT_MUTED,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

const PAGE_SIZE_OPTIONS = [8, 25, 50, 100];

interface PaginationBarProps {
  page: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (n: number) => void;
}
function PaginationBar({
  page,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: PaginationBarProps): JSX.Element {
  const nums = pageNumbers(page, totalPages);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        borderTop: `1px solid ${BORDER}`,
        flexWrap: 'wrap',
        gap: 10,
      }}
    >
      <span style={{ fontSize: 13, color: TEXT_MUTED }}>
        Page {page} of {totalPages}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <PageArrowButton disabled={page === 1} onClick={() => onPageChange(1)}>
          <ChevronDoubleIcon flip />
        </PageArrowButton>
        <PageArrowButton disabled={page === 1} onClick={() => onPageChange(page - 1)}>
          <ChevronIcon flip />
        </PageArrowButton>
        {nums.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onPageChange(n)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              fontSize: 12,
              fontWeight: n === page ? 700 : 500,
              color: n === page ? TEXT_DARK : TEXT_MUTED,
              background: n === page ? '#fff' : 'transparent',
              border: n === page ? `1px solid ${BORDER_CARD}` : '1px solid transparent',
              cursor: 'pointer',
            }}
          >
            {n}
          </button>
        ))}
        <PageArrowButton disabled={page === totalPages} onClick={() => onPageChange(page + 1)}>
          <ChevronIcon />
        </PageArrowButton>
        <PageArrowButton disabled={page === totalPages} onClick={() => onPageChange(totalPages)}>
          <ChevronDoubleIcon />
        </PageArrowButton>
      </div>
      <select
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        style={{
          fontSize: 12,
          padding: '6px 10px',
          borderRadius: 6,
          border: `1px solid ${BORDER}`,
          background: '#fff',
          color: TEXT_DARK,
          cursor: 'pointer',
        }}
      >
        {PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}/Page
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type PickerState = { productId: string } | 'bulk' | null;

export default function ProductClassification(): JSX.Element {
  const { organizationId } = useAuth();
  const mobile = useWindowWidth() < 768;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [filter, setFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<ClassificationCounts>({
    total: 0,
    pending: 0,
    auto: 0,
    override: 0,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [picker, setPicker] = useState<PickerState>(null);
  const [saving, setSaving] = useState(false);
  const [successIds, setSuccessIds] = useState<Set<string>>(new Set());
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback((): void => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    classificationApi
      .getQueue(organizationId, { page, pageSize, filter, search })
      .then((data) => {
        if (signal.aborted) return;
        setProducts(data.items ?? []);
        setTotal(data.total ?? 0);
        if (data.counts) setCounts(data.counts);
      })
      .catch((e: Error) => {
        if (!signal.aborted) {
          setError(e.message);
          setErrorStatus(e instanceof ApiError ? e.status : null);
        }
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, [organizationId, page, pageSize, filter, search]);

  useEffect(load, [load]);

  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleAll(): void {
    setSelected(
      selected.size === products.length ? new Set() : new Set(products.map((p) => p.product_id)),
    );
  }

  async function handleClassify(productId: string, ticCode: string): Promise<void> {
    setSaving(true);
    try {
      await classificationApi.classify(productId, organizationId, ticCode);
      setSuccessIds((prev) => new Set([...prev, productId]));
      setTimeout(() => {
        setSuccessIds((prev) => {
          const n = new Set(prev);
          n.delete(productId);
          return n;
        });
        load();
      }, 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Classification failed');
    } finally {
      setSaving(false);
      setPicker(null);
    }
  }

  async function handleBulkAssign(ticCode: string): Promise<void> {
    setSaving(true);
    try {
      await classificationApi.classifyBulk(organizationId, [...selected], ticCode);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk assignment failed');
    } finally {
      setSaving(false);
      setSelected(new Set());
      setPicker(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const TABS: TabDef[] = [
    { key: 'all', label: 'All', count: counts.total, badgeColor: BLUE },
    { key: 'pending_review', label: 'Need Review', count: counts.pending, badgeColor: RED },
    { key: 'auto', label: 'Auto Classified', count: counts.auto, badgeColor: ORANGE },
    { key: 'override', label: 'Seller Overrides', count: counts.override, badgeColor: GREEN },
  ];

  return (
    <div
      style={{
        padding: '18px 22px',
        background: '#F8FAFC',
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 24, fontWeight: 500, color: TEXT_DARK }}>
          Products &amp; Classification
        </div>
        <div style={{ fontSize: 16, color: TEXT_MUTED, marginTop: 4 }}>
          Assign SST TIC codes so STAC can calculate the correct tax for each product and state.
        </div>
      </div>

      <div
        style={{ display: 'flex', flexWrap: mobile ? 'wrap' : 'nowrap', gap: 24, marginBottom: 20 }}
      >
        <div style={{ flex: mobile ? '0 0 calc(50% - 12px)' : 1, minWidth: 0 }}>
          <StatCard
            icon={<Box3Icon />}
            iconBg="#EBF8FF"
            iconColor={BLUE}
            label="Total Products"
            value={counts.total}
          />
        </div>
        <div style={{ flex: mobile ? '0 0 calc(50% - 12px)' : 1, minWidth: 0 }}>
          <StatCard
            icon={<EyeLineIcon />}
            iconBg="#FFEBEC"
            iconColor={RED}
            label="Needs Review"
            value={counts.pending}
          />
        </div>
        <div style={{ flex: mobile ? '0 0 calc(50% - 12px)' : 1, minWidth: 0 }}>
          <StatCard
            icon={<CheckboxCircleIcon />}
            iconBg="#E0FAEC"
            iconColor={GREEN}
            label="Auto-classified"
            value={counts.auto}
          />
        </div>
        <div style={{ flex: mobile ? '0 0 calc(50% - 12px)' : 1, minWidth: 0 }}>
          <StatCard
            icon={<UserLineIcon />}
            iconBg="#EFEBFF"
            iconColor={PURPLE}
            label="Seller Overrides"
            value={counts.override}
          />
        </div>
      </div>

      {counts.pending > 0 && !bannerDismissed && (
        <PendingAlertBanner
          count={counts.pending}
          onReviewNow={() => {
            setFilter('pending_review');
            setPage(1);
          }}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      <div
        style={{
          background: '#fff',
          border: `1px solid ${BORDER_CARD}`,
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <FilterBar
          tabs={TABS}
          filter={filter}
          onFilter={(key) => {
            setFilter(key);
            setPage(1);
            setSelected(new Set());
          }}
          search={searchInput}
          onSearch={setSearchInput}
          mobile={mobile}
        />

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 920 }}>
            {selected.size > 0 && (
              <BulkBar
                count={selected.size}
                busy={saving}
                onAssign={() => setPicker('bulk')}
                onClear={() => setSelected(new Set())}
              />
            )}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 20px 10px 0',
                background: '#F9FAFB',
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <div style={{ flex: COLS.checkbox, paddingLeft: 20 }}>
                <input
                  type="checkbox"
                  checked={selected.size === products.length && products.length > 0}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer', accentColor: BLUE }}
                />
              </div>
              {HEADERS.map((h) => (
                <div
                  key={h.label}
                  style={{
                    flex: h.flex,
                    fontSize: 10,
                    fontWeight: 700,
                    color: TEXT_MUTED,
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                  }}
                >
                  {h.label}
                </div>
              ))}
            </div>

            {loading ? (
              <LoadingRows />
            ) : error ? (
              <ErrorState message={error} errorStatus={errorStatus} onRetry={load} />
            ) : products.length === 0 ? (
              <EmptyState filter={filter} />
            ) : (
              <>
                {products.map((p) => (
                  <TableRow
                    key={p.product_id}
                    product={p}
                    isSelected={selected.has(p.product_id)}
                    isSuccess={successIds.has(p.product_id)}
                    onToggle={() => toggleSelect(p.product_id)}
                    onAccept={() => handleClassify(p.product_id, p.suggested_tic_code!)}
                    onChangeTic={() => setPicker({ productId: p.product_id })}
                    saving={saving}
                  />
                ))}
                <PaginationBar
                  page={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(n) => {
                    setPageSize(n);
                    setPage(1);
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {picker && (
        <TicPicker
          onClose={() => setPicker(null)}
          onSelect={(cat) =>
            picker === 'bulk'
              ? handleBulkAssign(cat.tic_code)
              : handleClassify((picker as { productId: string }).productId, cat.tic_code)
          }
        />
      )}
    </div>
  );
}
